import React, { ReactNode } from 'react';
import cn from 'classnames';
import Header from '@components/Header';
import { GlobeIcon, LockIcon, Logo } from '@repo/icons';
import styles from './SharedResourceUtilityBar.module.scss';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAuthContext } from '@repo/hooks';
import { SharedContact } from '@repo/utils/models/SharedContact';
import { itemListToString } from '@repo/utils/helpers/string';
import { SharedGroup } from '@repo/utils/models/SharedGroup';
import {
  SharedResourceType,
  supportedPermissionsByResourceType,
  labelsForPermissionsByResourceType,
} from '@repo/redux/reducers/data/shareStatuses';
import { Tooltip, Overlay } from '@repo/components/tooltips';

function supportedPermissionsForSharedResource(sharedResourceType: SharedResourceType) {
  return supportedPermissionsByResourceType[sharedResourceType];
}

type SharedResource = SharedContact | SharedGroup;

const InviteOnlyIcon = () => <LockIcon width={20} height={30} viewBox="4 0 16 24" />;
const PublicIcon = () => <GlobeIcon width={20} height={24} viewBox="4 0 16 24" />;

function MessageForSharer<P extends string>({
  sharedResource,
  sharedResourceType,
}: {
  sharedResource: SharedResource;
  sharedResourceType: SharedResourceType;
}) {
  const supportedPermissions = supportedPermissionsForSharedResource(sharedResourceType) as P[];
  const labelForPermission = (permission: P) =>
    (labelsForPermissionsByResourceType[sharedResourceType] as Record<P, string>)[permission];

  if (!sharedResource.sharedPublicly) {
    return (
      <Tooltip placement="left" overlay={<Overlay>Viewable only by you and invitees</Overlay>}>
        <span className={styles.tooltip}>
          <InviteOnlyIcon />
        </span>
      </Tooltip>
    );
  }

  if (sharedResource.publicPermissions?.length == supportedPermissions.length) {
    return (
      <Tooltip placement="left" overlay={<Overlay>Viewable by anyone with link</Overlay>}>
        <span className={styles.tooltip}>
          <PublicIcon />
        </span>
      </Tooltip>
    );
  }

  const exposedPermissionLabels = (sharedResource.publicPermissions as P[]).map(labelForPermission);
  const hiddenPermissionLabels = supportedPermissions
    .filter((permission: P) => !(sharedResource.publicPermissions as P[])?.includes(permission))
    .map(labelForPermission);

  return (
    <Tooltip
      placement="left"
      overlay={
        <Overlay>
          {itemListToString(hiddenPermissionLabels)} hidden&nbsp;
          <b>{itemListToString(exposedPermissionLabels)} viewable by anyone with link</b>
        </Overlay>
      }
    >
      <span className={styles.tooltip}>
        <PublicIcon />
      </span>
    </Tooltip>
  );
}

const messageForSharee = (sharedResource: SharedResource) => {
  if (!sharedResource.sharedPublicly) {
    return `Shared with you ${
      sharedResource.sharerDisplayName ? `by ${sharedResource.sharerDisplayName}` : ''
    }`;
  }

  return sharedResource.sharerDisplayName ? `Shared by ${sharedResource.sharerDisplayName}` : '';
};

export default function SharedResourceUtilityBar({
  className,
  sharedResource,
  sharedResourceType,
  children,
  logIn,
  after,
}: {
  className?: string;
  sharedResource: SharedResource;
  sharedResourceType: SharedResourceType;
  children: ReactNode;
  logIn: () => void;
  after?: ReactNode;
}) {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  let titleClassName = styles.title;
  let actions = <></>;

  if (!user) {
    titleClassName = undefined;
    actions = (
      <>
        <Header.Action
          className={styles.loginBtn}
          title="Log In"
          onClick={logIn}
          tooltip={false}
          asButton
        />
        <Header.Action
          title="Sign Up"
          className="btn-blue"
          onClick={() => navigate('/login', { state: { isRegister: true } })}
          tooltip={false}
          asButton
        />
      </>
    );
  } else if (sharedResource.sharerIsSelf) {
    actions = (
      <div className={cn(styles.sharerInfo, 'information')}>
        <span>Shared by you</span>
        <span className="information-item">
          <MessageForSharer
            sharedResource={sharedResource}
            sharedResourceType={sharedResourceType}
          />
        </span>
      </div>
    );
  } else {
    actions = (
      <div className={cn(styles.shareeInfo, 'information')}>{messageForSharee(sharedResource)}</div>
    );
  }

  return (
    <Header
      className={cn(styles.header, !user && styles.unauthenticatedUser, className)}
      actions={actions}
      actionsClassName={styles.actions}
      after={after}
      titleClassName={titleClassName}
      goBack
    >
      <Link className={styles.clayLogo} to="/">
        <Logo width={31} preserveAspectRatio="xMidYMid" title="Clay" />
      </Link>
      {children}
    </Header>
  );
}
